import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

const fieldCls = "bg-background border-border rounded-sm focus-visible:ring-[#007AFF]";
const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2 block";

export const ChangeEmailCard = () => {
  const { setUser } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { current_password: password, new_email: newEmail });
      setUser(data);
      setNewEmail("");
      setPassword("");
      toast.success("Email updated");
    } catch (e) {
      toast.error(errText(e, "Failed to update email"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] p-6 space-y-4" data-testid="change-email-card">
      <div className="flex items-center gap-2">
        <AtSign className="w-4 h-4 text-[#007AFF]" />
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider">Change email</h2>
      </div>
      <div>
        <Label className={labelCls}>New email</Label>
        <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
          data-testid="change-email-input" className={`${fieldCls} font-mono`} placeholder="you@company.com" />
      </div>
      <div>
        <Label className={labelCls}>Current password</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          data-testid="change-email-password-input" className={fieldCls} placeholder="••••••••" />
      </div>
      <Button onClick={submit} disabled={busy || !newEmail || !password} data-testid="change-email-btn"
        className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm font-semibold">
        {busy ? "Updating…" : "Update Email"}
      </Button>
    </div>
  );
};

export const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password changed");
    } catch (e) {
      toast.error(errText(e, "Failed to change password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] p-6 space-y-4" data-testid="change-password-card">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-[#007AFF]" />
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider">Change password</h2>
      </div>
      <div>
        <Label className={labelCls}>Current password</Label>
        <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
          data-testid="current-password-input" className={fieldCls} placeholder="••••••••" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="new-password-input" className={fieldCls} placeholder="Min 6 characters" />
        </div>
        <div>
          <Label className={labelCls}>Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="confirm-password-input" className={fieldCls} placeholder="Repeat new password" />
        </div>
      </div>
      <Button onClick={submit} disabled={busy || !current || !next || !confirm} data-testid="change-password-btn"
        className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm font-semibold">
        {busy ? "Changing…" : "Change Password"}
      </Button>
    </div>
  );
};
